import React, { useEffect, useState, useMemo } from 'react'
import classNames from 'classnames'
import {
  Button,
  Grid,
  Modal,
  OutlinedInput,
  makeStyles,
} from '@material-ui/core'
import { get, isEqual } from 'lodash'
import { emailPlaceholderRegex } from 'Utils/Regexp'
import usePrevious from 'Utils/Hooks/usePrevious'
import SubmitButton from 'Components/Form/Button'
import Form from 'Components/Form'
import EmojiPicker from 'Components/EmojiPicker'
import RichTextEditor from '../../../../../../../../../../../../Components/RichTextEditor'
import {
  optionsPlaceholderWithoutDeeplinks,
  optionsRedirection,
  PIPELINE_STAGE_ENUM,
} from '../../../../../../../options'
import GroupSelect from '../../../../../../GroupSelect'
import { useEditSequenceContext } from '../../../../context/EditSequenceContext'
import PlaceholderEditor from '../PlaceholderEditor'
import { styles } from './styles'
import SymbolsCounter from '../../../../../../../../../../../../Components/SymbolsCounter'

const useStyles = makeStyles(() => styles)

const normalizeDynamicValues = (value, key) => {
  return typeof value[key] === 'string' ? JSON.parse(value[key]) : value[key]
}

const formatDataToValuesAndErrors = template => {
  const keys = Object.keys(template)
  return keys.reduce((acc, key) => {
    if (key === 'customDynamicValues') {
      const value = normalizeDynamicValues(template, key)
      acc[key] = { value, error: false }
    } else if (['title', 'body', 'name', 'route'].includes(key)) {
      acc[key] = { value: template[key], error: false }
    }
    return acc
  }, {})
}

const parsePlaceholders = (placeholders, dynamicData) => {
  return placeholders.reduce((obj, placeholder) => {
    return { ...obj, [placeholder]: dynamicData[placeholder] }
  }, {})
}

const getSmsBodyLength = value => {
  if (!value) return 0
  if (!Array.isArray(value)) return 0
  return value.reduce((total, paragraph) => {
    if (
      paragraph &&
      paragraph.children &&
      paragraph.children[0] &&
      paragraph.children[0].text
    ) {
      return total + paragraph.children[0].text.length
    } else {
      return total
    }
  }, 0)
}

export const CreateNotificationOrSmsModal = ({
  open,
  controlModal,
  cityId,
  onSubmit,
  commId,
  commType,
}) => {
  const isNotification = useMemo(() => commType === 'notification', [commType])
  const maxBodyLength = useMemo(() => (isNotification ? 180 : 160), [
    isNotification,
  ])
  const initialValues = useMemo(() => {
    let initialValues = {
      name: { value: '', error: false },
      body: {
        value: isNotification
          ? ''
          : [
              {
                type: 'paragraph',
                align: 'left',
                children: [{ text: '' }],
              },
            ],
        error: false,
      },
    }
    if (isNotification) {
      initialValues.route = { value: '', error: false }
      initialValues.title = { value: '', error: false }
    }
    return initialValues
  }, [isNotification])
  const { editSequenceContextState } = useEditSequenceContext()
  const { sequence, stage, defaultDynamicValues } = editSequenceContextState
  const [insertedValue, setInsertedValue] = useState(null)
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [inputsData, setInputsData] = useState(initialValues)
  const [titleEmojiPicked, setTitleEmojiPicked] = useState(null)
  const [bodyEmojiPicked, setBodyEmojiPicked] = useState(null)
  const [lastClicked, setLastClicked] = useState({
    component: null,
    position: 0,
  })

  const redirectionsOptions = useMemo(
    () => optionsRedirection.filter(option => option.stages.includes(stage)),
    [stage],
  )
  const classes = useStyles()
  const commDataString = isNotification
    ? inputsData.body.value + inputsData.title.value
    : JSON.stringify(inputsData.body)
  const placeholders = commDataString.match(emailPlaceholderRegex)
  const customDynamicValues = get(inputsData, 'customDynamicValues.value', {})
  const [placeholdersState, setPlaceholdersState] = useState(
    parsePlaceholders(placeholders || [], customDynamicValues),
  )
  const prevPlaceholders = usePrevious(placeholders)
  const prevCustomDynamicValues = usePrevious(customDynamicValues)
  const placeholderChanged = !isEqual(placeholders, prevPlaceholders)
  const customDynamicValuesChanged = !isEqual(
    customDynamicValues,
    prevCustomDynamicValues,
  )

  useEffect(() => {
    if (placeholders && placeholderChanged) {
      setPlaceholdersState(state => parsePlaceholders(placeholders, state))
    }
  }, [placeholders, placeholderChanged])

  useEffect(() => {
    if (placeholders && customDynamicValues && customDynamicValuesChanged) {
      setPlaceholdersState(parsePlaceholders(placeholders, customDynamicValues))
    }
  }, [placeholders, customDynamicValues])

  useEffect(() => {
    setDisabled(
      Object.values(inputsData)
        .map(item => item.error)
        .some(item => item),
    )
  }, [inputsData])

  useEffect(() => {
    if (!commId) {
      setInputsData(initialValues)
    } else {
      const action = sequence.Action.find(
        action =>
          action.template.id === commId &&
          action.actionType.name.toLowerCase() === commType,
      )
      const newInputValues = formatDataToValuesAndErrors(action.template)
      const data = { ...initialValues, ...newInputValues }
      if (
        isNotification &&
        [
          PIPELINE_STAGE_ENUM.PRE_ACCESS,
          PIPELINE_STAGE_ENUM.BLOCKED,
          PIPELINE_STAGE_ENUM.WAITING_FOR_REVIEW,
        ].includes(stage) &&
        data.route.value !== 'openApp'
      ) {
        setInputsData({
          ...data,
          route: { value: 'openApp', error: false },
        })
      } else {
        setInputsData(data)
      }
    }
    open ? setIsOpen(true) : setIsOpen(false)
  }, [open, commId, stage])

  const handleChangeInputPlaceholder = ({ target }) => {
    setPlaceholdersState(prev => ({
      ...prev,
      [target.name]: target.value,
    }))
  }

  const handleClearInputPlaceholder = input => {
    setPlaceholdersState(prev => ({
      ...prev,
      [input]: '',
    }))
  }

  const handleInputInsert = (value, inputName) => {
    const input = document.getElementById(`${inputName}Input`)
    let pos = getCursorPosition(inputName)
    const front = input.value.substring(0, pos)
    const back = input.value.substring(pos, input.value.length)
    const newValue = front + value + back
    const event = {
      target: {
        value: newValue,
      },
    }
    handleChange({ type: inputName, event })
    setLastClicked(prev => ({ ...prev, position: pos + value.length }))
  }

  const getCursorPosition = component => {
    return document.getElementById(`${component}Input`).selectionStart
  }

  const handleInsertPlaceholder = value => {
    if (lastClicked.component === 'body') {
      if (isNotification) {
        if (inputsData.body.value.length + value.length <= maxBodyLength) {
          handleInputInsert(value, 'body')
        }
      } else if (
        getSmsBodyLength(inputsData.body.value) + value.length <=
        maxBodyLength
      ) {
        setInsertedValue(value)
      }
    } else if (
      lastClicked.component === 'title' &&
      inputsData.title.value.length + value.length <= 60
    ) {
      handleInputInsert(value, 'title')
    }
    setSelectedPlaceholder(value)
  }

  const handleEmojiPicked = (event, emoji, inputName) => {
    if (inputName === 'title') {
      setTitleEmojiPicked(emoji)
    }
    if (inputName === 'body') {
      setBodyEmojiPicked(emoji)
    }
  }

  const handleSubmit = () => {
    inputsData.customDynamicValues = {
      value: placeholdersState,
      error: false,
    }

    const newInputValues = Object.keys(inputsData).reduce((acc, key) => {
      acc[key] = {
        value: inputsData[key].value,
        error: ['customDynamicValues'].includes(key)
          ? false
          : !inputsData[key].value.length,
      }
      return acc
    }, {})
    const disabled = Object.values(newInputValues)
      .map(item => item.error)
      .some(item => item)

    if (disabled) {
      setInputsData(newInputValues)
    } else {
      setInsertedValue(null)
      onSubmit({
        inputsData,
        commId,
        commType,
      })
    }
  }

  const handleChange = ({ type, event }) => {
    setInputsData({
      ...inputsData,
      [type]: { value: event.target.value, error: !event.target.value },
    })
  }

  useEffect(() => {
    if (!titleEmojiPicked) return
    if (
      inputsData.title.value.length < 60 &&
      lastClicked.component === 'title'
    ) {
      handleInputInsert(titleEmojiPicked, 'title', 'emoji')
    }
    setTitleEmojiPicked(null)
  }, [titleEmojiPicked])

  useEffect(() => {
    if (!bodyEmojiPicked) return
    if (isNotification) {
      if (
        inputsData.body.value.length < maxBodyLength - 1 &&
        lastClicked.component === 'body'
      ) {
        handleInputInsert(bodyEmojiPicked, 'body', 'emoji')
      }
    } else if (getSmsBodyLength(inputsData.body.value) < maxBodyLength - 1) {
      setInsertedValue(bodyEmojiPicked)
    }

    setBodyEmojiPicked(null)
  }, [bodyEmojiPicked])

  const handleCancel = () => {
    setInsertedValue(null)
    controlModal({ open: false, id: null })
  }

  const errorComponent = error => {
    return error ? <div className={classes.error}>field is required</div> : null
  }

  return (
    <Form>
      <Modal open={open} onClose={handleCancel} disablePortal={true}>
        <div className={classes.body}>
          <span className={classes.title}>
            {commId ? 'Edit' : 'Create new'} {commType}
          </span>
          <div>
            <div
              className={classNames(
                classes.rowContainer,
                classes.wrapperSelect,
              )}
            >
              <span className={classes.label}>Name</span>
              <OutlinedInput
                placeholder="Name"
                value={inputsData.name.value}
                onChange={event => handleChange({ type: 'name', event })}
                className={classes.input}
                error={inputsData.name.error}
                onClick={() =>
                  setLastClicked({
                    component: 'name',
                    position: 0,
                  })
                }
              />
            </div>
            {errorComponent(inputsData.name.error)}
            {isNotification && (
              <React.Fragment>
                <div className={classNames(classes.rowContainer)}>
                  <GroupSelect
                    label="Select a redirection"
                    options={redirectionsOptions}
                    onChange={value =>
                      handleChange({
                        type: 'route',
                        event: { target: { value } },
                      })
                    }
                    value={inputsData.route.value}
                    disableDefaultValue={true}
                    variant="outlined"
                    selectClassName={classes.groupSelect}
                    labelClassName={classes.placeholdersLabel}
                    fullWidth
                  />
                </div>
                {errorComponent(inputsData.route.error)}
              </React.Fragment>
            )}

            <div className={classNames(classes.rowContainer)}>
              <GroupSelect
                label="Insert Placeholder"
                options={optionsPlaceholderWithoutDeeplinks}
                onChange={handleInsertPlaceholder}
                value={selectedPlaceholder}
                disableDefaultValue={true}
                variant="outlined"
                selectClassName={classes.groupSelect}
                labelClassName={classes.placeholdersLabel}
                fullWidth
              />
            </div>
          </div>

          <Grid
            container
            xs={12}
            direction="column"
            className={classes.inputContainer}
          >
            {isNotification && (
              <React.Fragment>
                <Grid className={classes.columnContainer}>
                  <span className={classes.titleAndBodyLabel}>
                    Notification Title:
                  </span>
                  <div className={classes.titleAndBodyInputContainer}>
                    <OutlinedInput
                      id={'titleInput'}
                      placeholder="Title"
                      className={classes.titleInput}
                      value={inputsData.title.value}
                      onChange={event => {
                        handleChange({ type: 'title', event })
                      }}
                      error={inputsData.title.error}
                      multiline
                      onClick={() =>
                        setLastClicked({
                          component: 'title',
                          position: getCursorPosition('title'),
                        })
                      }
                      inputProps={{ maxLength: 60, dir: 'auto' }}
                    />
                    <EmojiPicker
                      onEmojiClick={(e, { emoji }) =>
                        handleEmojiPicked(e, emoji, 'title')
                      }
                      className={classes.emojiPicker}
                    />
                  </div>
                  <SymbolsCounter
                    valueToCounter={inputsData.title.value}
                    symbolLimit="60"
                  />
                </Grid>
                {errorComponent(inputsData.title.error)}
              </React.Fragment>
            )}

            <Grid className={classes.columnContainer}>
              <span className={classes.titleAndBodyLabel}>
                {isNotification ? 'Notification' : 'Sms'} body text:
              </span>
              <div className={classes.titleAndBodyInputContainer}>
                {isNotification ? (
                  <OutlinedInput
                    id={'bodyInput'}
                    className={classes.bodyInput}
                    placeholder={`${
                      isNotification ? 'Notification' : 'Sms'
                    } body`}
                    value={inputsData.body.value}
                    onChange={event => {
                      handleChange({ type: 'body', event })
                    }}
                    error={inputsData.body.error}
                    multiline
                    onClick={() =>
                      setLastClicked({
                        component: 'body',
                        position: getCursorPosition('body'),
                      })
                    }
                    inputProps={{ maxLength: maxBodyLength, dir: 'auto' }}
                  />
                ) : isOpen ? (
                  <RichTextEditor
                    key={`${commType}-${commId}`}
                    index={`${commType}-${commId}`}
                    fontSize="12px"
                    rows="20"
                    textToInsert={insertedValue}
                    disableBorder
                    onChange={value =>
                      handleChange({
                        type: 'body',
                        event: { target: { value } },
                      })
                    }
                    value={inputsData.body.value}
                    currentValueLength={getSmsBodyLength(inputsData.body.value)}
                    maxLength={maxBodyLength}
                    onClick={() =>
                      setLastClicked({ component: 'body', position: null })
                    }
                  />
                ) : null}
                <EmojiPicker
                  onEmojiClick={(e, { emoji }) =>
                    handleEmojiPicked(e, emoji, 'body')
                  }
                  className={classes.emojiPicker}
                />
              </div>
              <SymbolsCounter
                valueToCounter={
                  isNotification
                    ? inputsData.body.value
                    : getSmsBodyLength(inputsData.body.value)
                }
                symbolLimit={maxBodyLength.toString()}
              />
            </Grid>
            {errorComponent(inputsData.body.error)}
          </Grid>

          <PlaceholderEditor
            placeholders={placeholders}
            defaultDynamicValues={defaultDynamicValues}
            customDynamicValues={placeholdersState}
            onClearInput={handleClearInputPlaceholder}
            onChangeInput={handleChangeInputPlaceholder}
          />
          <Grid
            container
            direction="row"
            alignItems="center"
            style={{ paddingTop: '10px' }}
            xs={12}
          >
            <Grid container xs justifyContent="space-between">
              <Button
                variant="text"
                onClick={handleCancel}
                style={{ marginRight: '16px' }}
                className={classes.button}
              >
                Cancel
              </Button>
              <SubmitButton
                className={classes.button}
                onClick={handleSubmit}
                disabled={disabled}
              >
                Save
              </SubmitButton>
            </Grid>
          </Grid>
        </div>
      </Modal>
    </Form>
  )
}
